import React, { useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { useLocation, useParams } from 'react-router-dom';
import { Upload, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';

import apiService from '../../services/api';
import { useFileTreeContext } from '../../context/FileTreeContext';

/** Mirrors ChatArea's validation so attach button + drag-drop accept the same set. */
const ALLOWED_EXTENSIONS = ['.pdf', '.html', '.htm', '.xbrl', '.xml', '.csv', '.xlsx', '.docx'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'text/html',
  'application/xhtml+xml',
  'text/xml',
  'application/xml',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_BYTES = 50 * 1024 * 1024; // 50MB — backend limit

function isAcceptableFile(file: File): boolean {
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  return ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
}

interface Props {
  /** Wrap the page content; drags over this child area trigger the overlay. */
  children: React.ReactNode;
  /** Called after at least one upload succeeds so the sidebar project counts
      and the projects index can refresh in-place without a page reload. */
  onUploadComplete?: () => void | Promise<void>;
}

/**
 * Window-spanning drop target for file uploads.
 *
 * Renders as a passthrough wrapper around the page outlet. While a drag is
 * over the page, a full-area overlay shows the drop hint. Drops route every
 * file through the existing `apiService.uploadDocument` pipeline — the same
 * one the 📎 attach button uses — so the file tree, polling, and audit log
 * behave identically whether the user clicked or dropped.
 *
 * Project context: if the current URL is `/p/:projectId/...`, every dropped
 * file is auto-assigned to that project. Otherwise it lands in the org-wide
 * library (consistent with the attach button's behavior on /c/new).
 *
 * Day 2a scope: immediate-upload-on-drop. Day 2b (deferred) adds an
 * attachment-strip pattern where dropped files queue up next to the textarea
 * and only upload when the user clicks Send.
 */
const DropZoneOverlay: React.FC<Props> = ({ children, onUploadComplete }) => {
  const { projectId } = useParams<{ projectId?: string }>();
  const location = useLocation();
  const { refreshTree, addSessionSource, trackUpload } = useFileTreeContext();

  // Drops happen on the page wrapper — but auto-attaching to chat scope only
  // makes sense on chat routes (`/c/*`, `/p/:id/c/*`). On the files page the
  // user wants to upload-and-list, not start a chat with the new doc.
  const isOnChatRoute = location.pathname.includes('/c/');

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Surface size / type rejections from the dropzone validator below.
      for (const r of rejectedFiles) {
        const tooBig = r.errors.some((e) => e.code === 'file-too-large');
        const wrongType = r.errors.some((e) => e.code === 'file-invalid-type');
        if (tooBig) {
          toast.error(`"${r.file.name}" חורג מ-50MB`);
        } else if (wrongType) {
          toast.error(`"${r.file.name}" — סוג קובץ לא נתמך (PDF / HTML / XBRL / CSV / XLSX בלבד)`);
        } else {
          toast.error(`"${r.file.name}" נדחה`);
        }
      }

      if (acceptedFiles.length === 0) return;

      const multi = acceptedFiles.length > 1;
      const toastId = toast.loading(
        multi ? `מעלה ${acceptedFiles.length} קבצים...` : `מעלה "${acceptedFiles[0].name}"...`,
      );

      let successCount = 0;
      const failed: string[] = [];

      for (const file of acceptedFiles) {
        try {
          const resp = await apiService.uploadDocument(file, projectId);
          successCount += 1;
          // Track processing app-wide so dropped files get the same progress
          // card + "מוכן" notification as attach-button uploads.
          trackUpload({
            documentId: resp.document_id,
            filename: file.name,
            status: resp.status,
            stage: 'הקובץ הועלה וממתין להתחלת עיבוד',
          });
          // On chat routes, attach the freshly-uploaded doc to the chat scope
          // automatically — as a *persistent* session source (kept across
          // turns), matching the attach-button behavior. Without this the chat
          // has no way to know the doc exists and W3 ai.chat answers
          // generically. Project-files page drops skip this.
          if (isOnChatRoute) {
            addSessionSource(`doc-${resp.document_id}`);
          }
        } catch (err: any) {
          const detail = err?.response?.data?.detail;
          failed.push(`${file.name}${typeof detail === 'string' ? ` (${detail})` : ''}`);
        }
      }

      // Refresh the file tree so the new docs show up immediately. The
      // existing ChatArea polling will pick up processing status from here.
      refreshTree();
      // Refresh project counts in sidebar / projects index so the user sees
      // the new document reflected in the per-project badge without reload.
      if (successCount > 0) onUploadComplete?.();

      if (failed.length === 0) {
        toast.success(
          multi
            ? `${successCount} קבצים הועלו${projectId ? ' לפרויקט' : ''} ונכנסו לעיבוד — נעדכן כשיהיו מוכנים`
            : `"${acceptedFiles[0].name}" הועלה${projectId ? ' לפרויקט' : ''} ונכנס לעיבוד — נעדכן כשיהיה מוכן`,
          { id: toastId },
        );
      } else if (successCount === 0) {
        toast.error(`העלאה נכשלה: ${failed.join(', ')}`, { id: toastId, duration: 6000 });
      } else {
        toast.error(`${successCount}/${acceptedFiles.length} הועלו. נכשלו: ${failed.join(', ')}`, {
          id: toastId,
          duration: 6000,
        });
      }
    },
    [projectId, refreshTree, onUploadComplete, isOnChatRoute, addSessionSource, trackUpload],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_BYTES,
    validator: (file) =>
      isAcceptableFile(file)
        ? null
        : { code: 'file-invalid-type', message: 'סוג קובץ לא נתמך' },
  });

  return (
    <div {...getRootProps()} className="flex-1 flex flex-col overflow-hidden relative">
      {children}

      {/* Full-area overlay rendered only during an active drag. The wrapper
          itself stays click-through when nothing is being dragged so it never
          interferes with normal interactions inside the page. */}
      {isDragActive && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none"
          dir="rtl"
        >
          <div className="absolute inset-0 bg-primary-600/10 backdrop-blur-sm" />
          <div className="relative bg-white border-2 border-dashed border-primary-400 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3 max-w-md mx-4">
            <div className="h-14 w-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
              <Upload className="h-7 w-7" />
            </div>
            <div className="text-lg font-semibold text-slate-900">שחרר את הקבצים כאן</div>
            <div className="text-sm text-slate-500 text-center leading-relaxed">
              {projectId
                ? 'הקבצים יישמרו ויסונכרנו לפרויקט הזה.'
                : 'הקבצים יישמרו בספרייה האישית שלך.'}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
              <FileText className="h-3.5 w-3.5" />
              <span>PDF · HTML · XBRL · CSV · XLSX · עד 50MB</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropZoneOverlay;
